import type {
    PickerItemData,
    PickerSectionData,
} from "@design-system/Picker/Picker.types";
import type { ReactNode } from "react";
import {
    makeItem,
    makeValue,
    type PickerPlaygroundFeatures,
    type PickerPlaygroundFlyoutScope,
} from "./PickerPlayground.lib";

const treeDescriptions: Record<string, string> = {
    Expenses: "Operating costs by category",
    Rent: "Office lease, monthly",
    Utilities: "Power, water and telecom",
    Electricity: "Grid supply",
    Telecom: "Connectivity providers",
    Internet: "Fibre line",
    Software: "Subscriptions and licences",
    Income: "Revenue accounts",
    Sales: "Product and service revenue",
    Equity: "Owner contributions",
};

const treeRightTexts: Record<string, string> = {
    Expenses: "$48,200",
    Rent: "$24,000",
    Utilities: "$6,400",
    Electricity: "$3,100",
    Telecom: "$2,200",
    Internet: "$1,400",
    Mobile: "$800",
    Water: "$1,100",
    Software: "$17,800",
    Income: "$120,500",
    Sales: "$118,000",
    Interest: "$2,500",
    Equity: "$50,000",
};

type TreeDecoration = Pick<
    PickerPlaygroundFeatures,
    "hasDescriptions" | "hasRightText"
> &
    Partial<Pick<PickerPlaygroundFeatures, "flyoutSearch" | "flyoutFooter">> & {
        /** Footer of the flyout levels the `flyoutFooter` scope selects. */
        renderFlyoutFooter?: (query: string) => ReactNode;
    };

const isParentItem = (item: PickerItemData<string>) =>
    item.children !== undefined && item.children.length > 0;

/**
 * Whether a parent's level is in scope: `first` is the level a top-level row
 * opens, `deepest` a level whose rows are all leaves. `all` is left to the
 * root's `flyoutDefaults` so the sandbox exercises both mechanisms.
 */
const isLevelInScope = ({
    scope,
    depth,
    item,
}: {
    scope: PickerPlaygroundFlyoutScope | undefined;
    depth: number;
    item: PickerItemData<string>;
}) => {
    switch (scope) {
        case "first":
            return depth === 0;
        case "deepest":
            return (item.children ?? []).every((child) => !isParentItem(child));
        default:
            return false;
    }
};

const decorateTree = (
    items: ReadonlyArray<PickerItemData<string>>,
    decoration: TreeDecoration,
    depth = 0,
): Array<PickerItemData<string>> =>
    items.map((item): PickerItemData<string> => {
        const description = decoration.hasDescriptions
            ? treeDescriptions[item.label]
            : undefined;
        const rightText = decoration.hasRightText
            ? treeRightTexts[item.label]
            : undefined;
        if (item.children !== undefined) {
            const isSearchable = isLevelInScope({
                scope: decoration.flyoutSearch,
                depth,
                item,
            });
            const hasFooter = isLevelInScope({
                scope: decoration.flyoutFooter,
                depth,
                item,
            });
            return {
                ...item,
                description,
                rightText,
                flyout: {
                    searchable: isSearchable ? true : undefined,
                    searchPlaceholder: isSearchable
                        ? `Search ${item.label.toLowerCase()}`
                        : undefined,
                    renderFooter: hasFooter
                        ? decoration.renderFlyoutFooter
                        : undefined,
                },
                children: decorateTree(item.children, decoration, depth + 1),
            };
        }
        return { ...item, description, rightText };
    });

/** Sections whose items are trees: the chart of accounts split by statement. */
export const buildTreeSections = (
    decoration: TreeDecoration,
): Array<PickerSectionData<string>> => {
    const tree = buildTreeItems(decoration);
    return [
        { title: "Profit and loss", items: tree.slice(0, 2), selectAll: true },
        { title: "Balance sheet", items: tree.slice(2), selectAll: true },
    ];
};

/** Chart-of-accounts style tree, three levels deep, decorated per the sandbox toggles. */
export const buildTreeItems = (
    decoration: TreeDecoration,
): Array<PickerItemData<string>> => decorateTree(chartOfAccounts, decoration);

const chartOfAccounts: Array<PickerItemData<string>> = [
    {
        value: makeValue("Expenses"),
        label: "Expenses",
        children: [
            makeItem({ label: "Rent" }),
            {
                value: makeValue("Utilities"),
                label: "Utilities",
                children: [
                    makeItem({ label: "Electricity" }),
                    {
                        value: makeValue("Telecom"),
                        label: "Telecom",
                        children: [
                            makeItem({ label: "Internet" }),
                            makeItem({ label: "Mobile" }),
                        ],
                    },
                    makeItem({ label: "Water" }),
                ],
            },
            makeItem({ label: "Software" }),
        ],
    },
    {
        value: makeValue("Income"),
        label: "Income",
        children: [
            makeItem({ label: "Sales" }),
            makeItem({ label: "Interest" }),
        ],
    },
    makeItem({ label: "Equity" }),
];
